import type { Logger } from "@bindery/logging";

export type SyncLogger = Pick<Logger, "event" | "error">;

export const BACKOFF_BASE_MS = 2_000;
export const BACKOFF_MAX_MS = 5 * 60_000;
const JITTER = 0.2;

/** 2 s, 4 s, 8 s … capped at 5 min, each ±20 %. */
export function backoffDelay(
  failures: number,
  random: () => number = Math.random,
): number {
  const base = Math.min(BACKOFF_BASE_MS * 2 ** (failures - 1), BACKOFF_MAX_MS);
  return Math.round(base * (1 + JITTER * (2 * random() - 1)));
}

export interface Clock {
  now: () => number;
  setTimer: (run: () => void, ms: number) => unknown;
  clearTimer: (timer: unknown) => void;
  random: () => number;
}

export const systemClock: Clock = {
  now: () => Date.now(),
  setTimer: (run, ms) => setTimeout(run, ms),
  clearTimer: (timer) => {
    clearTimeout(timer as ReturnType<typeof setTimeout>);
  },
  random: () => Math.random(),
};

export interface RetryGate {
  /** True while a backoff is running; the scheduled retry will flush. */
  readonly waiting: () => boolean;
  readonly failed: (retry: () => void) => number;
  readonly succeeded: () => void;
  readonly reset: () => void;
}

export function createRetryGate(clock: Clock): RetryGate {
  let failures = 0;
  let timer: unknown = null;

  function clear(): void {
    if (timer !== null) clock.clearTimer(timer);
    timer = null;
  }

  return {
    waiting: () => timer !== null,
    failed(retry) {
      failures += 1;
      const delay = backoffDelay(failures, clock.random);
      clear();
      timer = clock.setTimer(() => {
        timer = null;
        retry();
      }, delay);
      return delay;
    },
    succeeded() {
      failures = 0;
    },
    reset() {
      failures = 0;
      clear();
    },
  };
}
